import type { IClimateMonitoringSignalResult } from './interfaces/IMonitoringSignalResult';

/** Ported from docs/mockups/ai-agents/climate-monitoring/data/demo-data.embed.js */
const CLIMATE_DEMO_SIGNALS: Record<string, IClimateMonitoringSignalResult> = {
  'run-cli-001-02': {
    summary: {
      total_findings: 5,
      high_risk_count: 5,
      entities_monitored: 4,
      entities_with_active_hazard_count: 4,
      themes_frequency: {
        flood: 2,
        storm: 1,
        'animal-disease': 1,
        'trade-sanctions': 1,
      },
    },
    findings: [
      {
        id: 'cli-001',
        entity_kind: 'supplier',
        entity_name: 'Polymer Technologies',
        entity_unit_id: 47426,
        related_materials: ['Polyethylene Film', 'Adhesive Materials'],
        location: { country: 'Germany', region: 'North Rhine-Westphalia', locality: 'Cologne area' },
        theme: 'flood',
        title: 'River level forecast exceeds 10-year threshold near plant',
        severity: 'high',
        confidence: 0.86,
        observed_at: '2026-07-18',
        horizon: '72h',
        source_type: 'hydrology',
        source_label: 'National hydrology service',
        source_url: 'https://example.com/hydro/nrw-crest',
        shortage_risk: {
          level: 'elevated',
          summary: 'Flood access risk raises delay probability on polymer film shipments into Tier-1 Input Products.',
        },
        recommended_action: 'Confirm open POs and logistics contingency for Polyethylene Film this week.',
        impact_trail: [
          { role: 'Hazard', entity_name: 'Flood advisory — NRW river crest' },
          { role: 'Supplier', entity_name: 'Polymer Technologies' },
          { role: 'Input Product', entity_name: 'Polyethylene Film' },
          { role: 'Own Operations', entity_name: 'Dublin manufacturing' },
          { role: 'Impact', entity_name: 'Shortage / downtime risk' },
        ],
      },
      {
        id: 'cli-002',
        entity_kind: 'own-operation',
        entity_name: 'Attaima Internal Manufacturing — Dublin',
        entity_unit_id: 51002,
        related_materials: ['Dairy Protein Isolate', 'Polyethylene Film'],
        location: { country: 'Ireland', region: 'Dublin', locality: null },
        theme: 'storm',
        title: 'Severe wind advisory may disrupt inbound logistics and site operations',
        severity: 'high',
        confidence: 0.81,
        observed_at: '2026-07-18',
        horizon: '48h',
        source_type: 'meteo',
        source_label: 'National weather service',
        source_url: 'https://example.com/meteo/dublin-wind',
        shortage_risk: {
          level: 'elevated',
          summary:
          'Own Operations downtime risk while Input Product inbound and Output Product outbound '
          + 'are weather-constrained.',
        },
        recommended_action: 'Confirm staffing / shift plans; prepare Impact Scenario if plant capacity drops.',
        impact_trail: [
          { role: 'Hazard', entity_name: 'Storm / wind advisory — Dublin' },
          { role: 'Own Operations', entity_name: 'Dublin manufacturing' },
          { role: 'Input Products', entity_name: 'Dairy Protein Isolate · Polyethylene Film' },
          { role: 'Impact', entity_name: 'Site downtime risk' },
        ],
      },
      {
        id: 'cli-003',
        entity_kind: 'balance-sheet-asset',
        entity_name: 'Cork Filling Line Asset',
        entity_unit_id: 52010,
        related_materials: ['Finished Device Pack'],
        location: { country: 'Ireland', region: 'Cork', locality: null },
        theme: 'flood',
        title: 'Flash-flood watch near Balance Sheet Asset site',
        severity: 'critical',
        confidence: 0.88,
        observed_at: '2026-07-17',
        horizon: '24h',
        source_type: 'hydrology',
        source_label: 'National hydrology service',
        source_url: 'https://example.com/hydro/cork-flash',
        shortage_risk: {
          level: 'critical',
          summary:
          'Owned plant asset may be inaccessible; Output Product throughput at risk even if suppliers '
          + 'are clear.',
        },
        recommended_action: 'Escalate site contingency; open Impact Scenario for Cork asset footprint.',
        impact_trail: [
          { role: 'Hazard', entity_name: 'Flash-flood watch — Cork' },
          { role: 'Balance Sheet Asset', entity_name: 'Cork Filling Line Asset' },
          { role: 'Output / material', entity_name: 'Finished Device Pack' },
          { role: 'Impact', entity_name: 'Throughput / allocation risk' },
        ],
      },
      {
        id: 'cli-004',
        entity_kind: 'supplier',
        entity_name: 'AgriFeed Co-op',
        entity_unit_id: 48001,
        related_materials: ['Dairy Protein Isolate'],
        location: { country: 'Netherlands', region: 'Gelderland', locality: null },
        theme: 'animal-disease',
        title: 'Regional livestock disease advisory affecting dairy catchment',
        severity: 'critical',
        confidence: 0.9,
        observed_at: '2026-07-17',
        horizon: '14d',
        source_type: 'veterinary',
        source_label: 'National veterinary authority',
        source_url: 'https://example.com/vet/advisory-2026-07',
        shortage_risk: {
          level: 'critical',
          summary:
          'Dairy protein Input Products may face allocation or quarantine holds for Own Operations '
          + 'consuming them.',
        },
        recommended_action: 'Escalate dual-source readiness; prepare Impact Scenario for Dairy Protein Isolate.',
        impact_trail: [
          { role: 'Hazard', entity_name: 'Livestock disease advisory' },
          { role: 'Supplier', entity_name: 'AgriFeed Co-op' },
          { role: 'Input Product', entity_name: 'Dairy Protein Isolate' },
          { role: 'Own Operations', entity_name: 'Dublin manufacturing' },
          { role: 'Impact', entity_name: 'Material shortage risk' },
        ],
      },
      {
        id: 'cli-005',
        entity_kind: 'supplier',
        entity_name: 'AgriFeed Co-op',
        entity_unit_id: 48001,
        related_materials: ['Dairy Protein Isolate'],
        location: { country: 'Netherlands', region: null, locality: null },
        theme: 'trade-sanctions',
        title: 'Export certification tightening after disease outbreak',
        severity: 'high',
        confidence: 0.78,
        observed_at: '2026-07-18',
        horizon: '30d',
        source_type: 'trade',
        source_label: 'Trade ministry advisory',
        source_url: 'https://example.com/trade/export-hold',
        shortage_risk: {
          level: 'elevated',
          summary: 'Cross-border dairy shipments may be delayed pending phytosanitary / veterinary certificates.',
        },
        recommended_action: 'Track certificate status with supplier; model Impact Scenario if holds persist.',
        impact_trail: [
          { role: 'Hazard', entity_name: 'Export certification / trade hold' },
          { role: 'Supplier', entity_name: 'AgriFeed Co-op' },
          { role: 'Input Product', entity_name: 'Dairy Protein Isolate' },
          { role: 'Impact', entity_name: 'Border delay / shortage risk' },
        ],
      },
    ],
    entities: [
      {
        unitId: 47426,
        entity_kind: 'supplier',
        name: 'Polymer Technologies',
        exposure_score: 72,
        trend: 'rising',
        spike: true,
        location: { country: 'Germany', region: 'North Rhine-Westphalia', locality: 'Cologne area' },
        related_materials: ['Polyethylene Film', 'Adhesive Materials'],
        climate_profile: {
          hazards_active: ['flood', 'heat'],
          dominant_horizon: '72h',
          water_stress_index: 0.62,
          notes: 'Plant adjacent to floodplain corridor',
        },
        finding_ids: ['cli-001'],
        alerts: [
          {
            theme: 'Flood', title: 'River crest advisory', severity: 'high', source: 'National hydrology service',
          },
        ],
      },
      {
        unitId: 51002,
        entity_kind: 'own-operation',
        name: 'Attaima Internal Manufacturing — Dublin',
        exposure_score: 68,
        trend: 'rising',
        spike: true,
        location: { country: 'Ireland', region: 'Dublin', locality: null },
        related_materials: ['Dairy Protein Isolate', 'Polyethylene Film'],
        climate_profile: {
          hazards_active: ['storm'],
          dominant_horizon: '48h',
          water_stress_index: null,
          notes: 'Own Operations site consuming upstream Input Products',
        },
        finding_ids: ['cli-002'],
        alerts: [
          {
            theme: 'Storm', title: 'Severe wind advisory', severity: 'high', source: 'National weather service',
          },
        ],
      },
      {
        unitId: 52010,
        entity_kind: 'balance-sheet-asset',
        name: 'Cork Filling Line Asset',
        exposure_score: 84,
        trend: 'rising',
        spike: true,
        location: { country: 'Ireland', region: 'Cork', locality: null },
        related_materials: ['Finished Device Pack'],
        climate_profile: {
          hazards_active: ['flood'],
          dominant_horizon: '24h',
          water_stress_index: 0.71,
          notes: 'Balance Sheet Asset — physical plant footprint',
        },
        finding_ids: ['cli-003'],
        alerts: [
          {
            theme: 'Flood', title: 'Flash-flood watch', severity: 'critical', source: 'National hydrology service',
          },
        ],
      },
      {
        unitId: 48001,
        entity_kind: 'supplier',
        name: 'AgriFeed Co-op',
        exposure_score: 91,
        trend: 'rising',
        spike: true,
        location: { country: 'Netherlands', region: 'Gelderland', locality: null },
        related_materials: ['Dairy Protein Isolate'],
        climate_profile: {
          hazards_active: ['animal-disease', 'trade-sanctions'],
          dominant_horizon: '14d',
          water_stress_index: 0.4,
          notes: 'Dairy catchment under veterinary + trade pressure',
        },
        finding_ids: ['cli-004', 'cli-005'],
        alerts: [
          {
            theme: 'Animal disease',
            title: 'Livestock disease advisory',
            severity: 'critical',
            source: 'National veterinary authority',
          },
          {
            theme: 'Trade sanctions',
            title: 'Export certification tightening',
            severity: 'high',
            source: 'Trade ministry advisory',
          },
        ],
      },
    ],
  },
};

CLIMATE_DEMO_SIGNALS['run-cli-001-01'] = {
  summary: {
    total_findings: 2,
    high_risk_count: 1,
    entities_monitored: 4,
    entities_with_active_hazard_count: 2,
    themes_frequency: { flood: 1, storm: 1 },
  },
  findings: CLIMATE_DEMO_SIGNALS['run-cli-001-02'].findings.slice(0, 2),
  entities: CLIMATE_DEMO_SIGNALS['run-cli-001-02'].entities.slice(0, 2),
};

CLIMATE_DEMO_SIGNALS['501'] = CLIMATE_DEMO_SIGNALS['run-cli-001-02'];

export default CLIMATE_DEMO_SIGNALS;
